import type React from "react"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useTheme } from "../theme-context"
import {
	RAIL_CONTROL_CLASS,
	RAIL_CONTROL_ICON_CLASS,
	RAIL_ROW_EXPANDED_CLASS,
	RailTooltip,
} from "./rail-controls"

const SunIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		className="w-4 h-4"
		aria-hidden="true"
	>
		<circle cx="8" cy="8" r="3" />
		<path d="M8 1.5v1.5M8 13v1.5M1.5 8H3M13 8h1.5M3.4 3.4l1 1M11.6 11.6l1 1M12.6 3.4l-1 1M4.4 11.6l-1 1" />
	</svg>
)

const MoonIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-4 h-4"
		aria-hidden="true"
	>
		<path d="M13.5 9.5A6 6 0 0 1 6.5 2.5a6 6 0 1 0 7 7Z" />
	</svg>
)

const AutoIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-4 h-4"
		aria-hidden="true"
	>
		<circle cx="8" cy="8" r="6" />
		<path d="M8 2a6 6 0 0 0 0 12Z" fill="currentColor" stroke="none" />
	</svg>
)

const MODE_ICONS = {
	light: SunIcon,
	dark: MoonIcon,
	auto: AutoIcon,
} as const

/**
 * Cycles light → dark → auto (ZPL-101). The control is named "Theme"
 * everywhere — the mobile More-menu entry, the 88px rail and the 264px rail
 * — so a voice-control user says the same thing regardless of a persisted UI
 * state. `compact` renders the icon-only variant for the rail (ZPL-348);
 * `expanded` swaps its tooltip for a visible left-aligned "Theme" label. The
 * accessible name keeps the "Theme: <mode>" shape in every variant: the
 * visible text verbatim (WCAG 2.5.3 label-in-name) plus the mode, which
 * otherwise reaches screen readers only through the aria-hidden icon.
 */
export function ThemePill({
	compact = false,
	expanded = false,
}: {
	compact?: boolean
	expanded?: boolean
}) {
	const { mode, cycleMode } = useTheme()
	const { locale } = useLocale()
	const t = getTranslations(locale)

	if (compact) {
		const Icon = MODE_ICONS[mode]
		const label = `${t.theme.toggleLabel}: ${t.theme[mode]}`

		if (expanded) {
			return (
				<button
					type="button"
					onClick={cycleMode}
					aria-label={label}
					className={RAIL_ROW_EXPANDED_CLASS}
				>
					<span aria-hidden="true" className={RAIL_CONTROL_ICON_CLASS}>
						<Icon />
					</span>
					<span className="truncate text-sm text-muted-foreground">
						{t.theme.toggleLabel}
					</span>
				</button>
			)
		}

		return (
			<button
				type="button"
				onClick={cycleMode}
				aria-label={label}
				className={RAIL_CONTROL_CLASS}
			>
				<span aria-hidden="true" className={RAIL_CONTROL_ICON_CLASS}>
					<Icon />
				</span>
				<RailTooltip label={label} />
			</button>
		)
	}

	return (
		<button
			type="button"
			onClick={cycleMode}
			aria-label={`${t.theme.toggleLabel}: ${t.theme[mode]}`}
			className="rounded-sm border border-border px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:border-foreground/25 transition-colors"
		>
			{t.theme[mode]}
		</button>
	)
}
